import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import PayrollTypeForm from "@/modules/payrollTypes/components/PayrollTypeForm";
import { usePayrollTypesDictionary } from "@/modules/payrollTypes/hooks/usePayrollTypesDictionary";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { ApiError } from "@/services/api";
import { ReadPayrollType, CreatePayrollType } from "@/types/payrollType";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function PayrollTypeCreator() {
  const id = "new";
  const { t } = useTranslation();
  const config = usePayrollTypesDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { invalidateListQuery } = useQueryCacheUpdater(Module.PayrollTypes);
  const { can_create } = useModulePermissions(Module.PayrollTypes);
  const labels = config.getLabels(t);

  const mutation = useMutation<ReadPayrollType, ApiError, CreatePayrollType>({
    mutationFn: async (values) => await config.service.post(values),
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      invalidateListQuery();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<CreatePayrollType>({
    resolver: zodResolver(config.schema),
    defaultValues: config.defaultValues,
    disabled: mutation.isPending || !can_create,
  });

  useFormUnsavedChangesWarning(form);

  return (
    <DictionaryRecordContextProvider
      value={{
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      <PayrollTypeForm mode="create" />
    </DictionaryRecordContextProvider>
  );
}
